<script setup lang="ts">
import { Code, LayoutDashboard, FileText, Monitor, Server, Users, Sparkles, MessagesSquare, ArrowRight, Brain, RefreshCw, Globe, ShieldCheck, Wrench } from 'lucide-vue-next'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '~/components/ui/hover-card'
import { HoverCardArrow } from 'reka-ui'

const services = [
  {
    title: 'Laravel Apps',
    icon: Code,
    link: '/services/laravel',
    summary: 'Custom web applications on Laravel, the PHP framework we trust most.',
    highlights: ['SaaS platforms & portals', 'REST & GraphQL APIs', 'Queue-driven processing', 'Payment integrations'],
  },
  {
    title: 'Admin Panels',
    icon: LayoutDashboard,
    link: '/services/filamentphp',
    summary: 'Back-office dashboards and admin interfaces built rapidly with FilamentPHP.',
    highlights: ['Custom admin panels', 'Multi-resource dashboards', 'Bulk actions & exports', 'Role-based access control'],
  },
  {
    title: 'CMS Platforms',
    icon: FileText,
    link: '/services/cms-platforms',
    summary: 'Content-managed sites that give editors control and developers clean code.',
    highlights: ['Statamic, Craft & OctoberCMS', 'Flexible content structures', 'Multi-site & multi-language', 'Headless API delivery'],
  },
  {
    title: 'Vue.js & Nuxt',
    icon: Monitor,
    link: '/services/vue-nuxt',
    summary: 'Dynamic frontends with SSR, real-time features, and Laravel integration.',
    highlights: ['Server-side rendering', 'Laravel Inertia integration', 'Component design systems', 'TypeScript throughout'],
  },
  {
    title: 'DevOps',
    icon: Server,
    link: '/services/devops',
    summary: 'Infrastructure, CI/CD, and server management so you focus on your business.',
    highlights: ['Server provisioning', 'CI/CD pipelines', 'DNS, SSL & firewalls', 'Monitoring & backups'],
  },
  {
    title: 'Team Integration',
    icon: Users,
    link: '/services/team-integration',
    summary: 'We slot into your workflow: your tools, your process, your pace.',
    highlights: ['Agile / Scrum / Kanban', 'Code reviews & pairing', 'Slack, Jira, Notion', 'Documentation & handover'],
  },
  {
    title: 'AI-Augmented',
    icon: Brain,
    link: '/services/ai-development',
    summary: 'AI tools for faster development and better code quality, with human oversight.',
    highlights: ['AI-assisted code generation', 'Automated test writing', 'Rapid prototyping', 'Security scanning'],
  },
  {
    title: 'Consulting',
    icon: MessagesSquare,
    link: '/services/consulting',
    summary: 'Architecture, scaling strategy, and technical guidance when you need expert input.',
    highlights: ['Architecture design', 'Scaling strategy', 'Technical due diligence'],
  },
  {
    title: 'CMS Migration',
    icon: RefreshCw,
    link: '/cms-migration',
    summary: 'Move from WordPress, Drupal, or MODX to Laravel-based platforms without losing content or SEO.',
    highlights: ['WordPress to Laravel', 'Drupal to Statamic', 'MODX to Laravel', 'Full SEO preservation'],
  },
  {
    title: 'REST APIs',
    icon: Globe,
    link: '/services/api-development',
    summary: 'RESTful and GraphQL APIs that connect your systems, apps, and third-party services.',
    highlights: ['REST & GraphQL endpoints', 'Third-party integrations', 'OAuth & token auth', 'API documentation'],
  },
  {
    title: 'Testing & QA',
    icon: ShieldCheck,
    link: '/services/testing-qa',
    summary: 'Automated test suites and quality assurance to ship with confidence.',
    highlights: ['PHPUnit & Pest', 'Browser testing with Dusk', 'CI test pipelines', 'Code review & standards'],
  },
  {
    title: 'Maintenance',
    icon: Wrench,
    link: '/ongoing-support',
    summary: 'Monthly retainers to keep your application secure, updated, and running smoothly.',
    highlights: ['Security patches', 'Dependency updates', 'Performance monitoring', 'Priority bug fixes'],
  },
]
</script>

<template>
  <section id="services" class="py-20 md:py-40 relative">

    <div class="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
      <div class="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
        <!-- Left: Content -->
        <div>
          <div class="flex items-center gap-3 mb-6">
            <span data-aos="fade" class="text-xs tracking-[0.25em] text-blu-900 dark:text-blu-400 uppercase font-medium">Services</span>
          </div>

          <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-700 dark:text-white mb-6"
           data-aos="fade"
            data-aos-delay="200">
            Laravel development &amp; CMS specialists
          </h1>

          <p data-aos="fade" data-aos-delay="200" class="text-lg md:text-xl text-slate-700/80 dark:text-white/65 leading-relaxed text-pretty max-w-xl mb-10">
            We build and maintain web applications in Laravel, Vue.js and Nuxt: custom apps, admin panels, CMS migrations, hosting and ongoing support, all under one roof.
          </p>

          <NuxtLink
            data-aos="fade"
            data-aos-delay="400"
            to="/services"
            class="font-medium text-blu-600 underline decoration-dotted underline-offset-4 transition-colors hover:decoration-solid dark:text-blu-400"
          >
            See everything we offer
          </NuxtLink>
        </div>

        <!-- Right: the services in an open grid, divided only by hairlines
             between them (the last row and column's own lines are clipped
             off), on the same dot stage as the migration and support visuals. -->
        <VisualStage>
          <div class="relative overflow-hidden">
            <div class="-mb-px -mr-px grid grid-cols-3 md:grid-cols-4">
          <HoverCard
            v-for="(service, index) in services"
            :key="service.title"
            :open-delay="200"
            :close-delay="100"
          >
            <HoverCardTrigger as-child>
              <!-- Each box is the link to its service; the card opens on hover. -->
              <NuxtLink
                :to="service.link"
                data-aos="fade"
                :data-aos-delay="150 + index * 75"
                class="service-card group relative flex flex-col items-center justify-center aspect-square p-4 border-b border-r border-slate-900/10 dark:border-white/10 transition-colors duration-300 hover:bg-blu-500/[0.06] focus-visible:bg-blu-500/[0.06] focus-visible:outline-none"
              >
                <component :is="service.icon" class="service-icon w-6 h-6 text-blu-600 dark:text-blu-400/60 mb-2 relative z-10 transition-all duration-500 group-hover:scale-110 group-hover:text-blu-600 dark:group-hover:text-blu-400" />
                <span class="text-xs text-slate-700/80 dark:text-white/60 text-center leading-tight relative z-10 transition-colors duration-300 group-hover:text-blu-600 dark:group-hover:text-blu-400">{{ service.title }}</span>
              </NuxtLink>
            </HoverCardTrigger>
            <HoverCardContent
              side="top"
              :side-offset="12"
              class="w-72 p-0 bg-dark-500/95 dark:bg-dark-500/80 backdrop-blur-md border border-white/[0.08] rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
            >
              <div class="overflow-hidden rounded-xl">
                <div class="px-5 pt-5 pb-4">
                  <p class="text-[13px] text-white/80 leading-relaxed mb-4">{{ service.summary }}</p>
                  <div class="space-y-2">
                    <div v-for="point in service.highlights" :key="point" class="flex items-start gap-2.5">
                      <span class="w-1 h-1 rounded-full bg-blu-400/70 mt-1.5 flex-shrink-0" />
                      <span class="text-xs text-white/50 leading-relaxed">{{ point }}</span>
                    </div>
                  </div>
                </div>
                <NuxtLink :to="service.link" class="flex items-center justify-between px-5 py-3 border-t border-white/[0.06] hover:bg-blu-500/[0.08] transition-colors">
                  <span class="text-xs font-medium text-blu-600 dark:text-blu-400">Learn more<span class="sr-only"> about {{ service.title }}</span></span>
                  <ArrowRight class="w-3.5 h-3.5 text-blu-600 dark:text-blu-400" />
                </NuxtLink>
              </div>
              <HoverCardArrow as-child :width="14" :height="7">
                <svg class="block" viewBox="0 0 14 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 0L7 7L14 0" fill="#0f0f1a" fill-opacity="0.95" stroke="rgba(255,255,255,0.08)" stroke-width="1" />
                </svg>
              </HoverCardArrow>
            </HoverCardContent>
          </HoverCard>
            </div>
          </div>
        </VisualStage>
      </div>
    </div>
  </section>
</template>

<style scoped>
.service-card:hover .service-icon {
  animation: chromaShift 2.5s ease-in-out infinite;
}

@keyframes chromaShift {
  0%, 100% {
    filter: drop-shadow(1px 0 0 rgba(255, 0, 80, 0.5))
            drop-shadow(-1px 0 0 rgba(0, 200, 255, 0.5));
  }
  25% {
    filter: drop-shadow(0 1px 0 rgba(0, 200, 255, 0.5))
            drop-shadow(0 -1px 0 rgba(255, 0, 200, 0.5));
  }
  50% {
    filter: drop-shadow(-1px 0 0 rgba(120, 0, 255, 0.5))
            drop-shadow(1px 0 0 rgba(0, 255, 120, 0.5));
  }
  75% {
    filter: drop-shadow(0 -1px 0 rgba(255, 200, 0, 0.5))
            drop-shadow(0 1px 0 rgba(0, 100, 255, 0.5));
  }
}
</style>
